import {
  FormControl,
  FormLabel,
  Input,
  Box,
  Icon,
  IconButton,
  InputGroup,
  InputRightElement,
  Text,
  Textarea,
} from "@chakra-ui/react";
import {BsPlusCircleDotted} from "react-icons/bs";
import {AiFillDelete} from "react-icons/ai";
import {useEffect, useRef, useState} from "react";
import {drugAutocomplete} from "../../../crudRequests";
import {SearchIcon} from "@chakra-ui/icons";
import {toMedicalList} from "../../../lib/patientMedicalLists";

export default function UnlimitedItemFields({
  label,
  items,
  setItems,
  fields,
  dashState,
}) {
  // A patient created from a referral intake stores the free-text answer as a
  // STRING here ("N/A"), which is truthy and has no `.map` — it blanked this
  // whole screen for the patient themselves. Coerce on read; the first save
  // writes the canonical list back.
  const list = toMedicalList(items);

  const handleOnChange = (index, field, value) => {
    const updatedItems = list.map((entry, i) =>
      i === index ? {...entry, [field]: value} : entry
    );
    setItems(updatedItems);
  };
  const handleRemove = (index) => {
    const updatedItems = list.filter((_, i) => i !== index);
    setItems(updatedItems);
  };
  return (
    <FormControl id={label} isDisabled={!items} mt="5">
      <FormLabel textAlign={"center"}>{label}</FormLabel>
      {list.map((item, index) => (
          <Box display="flex" key={index} mt={2}>
            <Box flex={1} mr={2}>
              <Box
                gap={2}
                display={{base: "block", sm: "flex"}}
                textAlign={"left"}
              >
                {fields.map(({name, placeholder, type}) => {
                  if (type === "medicationSelect") {
                    return (
                      <MedicationAutoComplete
                        dashState={dashState}
                        initialValue={item[name]}
                        onChange={(e) => {
                          handleOnChange(index, name, e.brand_name);
                        }}
                      />
                    );
                  }
                  return (
                    <Input
                      key={name}
                      size="sm"
                      variant="outline"
                      borderRadius="md"
                      type={type}
                      placeholder={placeholder}
                      value={item[name]}
                      onChange={(e) =>
                        handleOnChange(index, name, e.target.value)
                      }
                    />
                  );
                })}
              </Box>
              <Textarea
                key="note"
                placeholder="Note"
                resize="none"
                size="sm"
                variant="outline"
                borderRadius="md"
                mt={2}
                value={item.note}
                onChange={(e) => handleOnChange(index, "note", e.target.value)}
              />
            </Box>
            <Box display="flex" alignItems="center">
              <IconButton
                icon={<Icon as={AiFillDelete} />}
                aria-label="Remove Entry"
                onClick={() => handleRemove(index)}
                size="xs"
                variant="ghost"
                colorScheme="gray"
              />
            </Box>
          </Box>
        ))}
      <Icon
        cursor="pointer"
        onClick={() => {
          const newItem = fields.reduce((acc, f) => {
            acc[f.name] = "";
            return acc;
          }, {});
          setItems([...list, newItem]);
        }}
        as={BsPlusCircleDotted}
        fontSize={"26px"}
        mt={"4"}
        display="block"
        mx="auto"
      ></Icon>
    </FormControl>
  );
}

function MedicationAutoComplete({dashState, onChange, initialValue}) {
  const [possibleDrugs, setPossibleDrugs] = useState([]);
  const timeout = useRef();
  const firstRender = useRef(true);
  const [drugName, setDrugName] = useState(initialValue);
  const [selectedDrug, setSelectedDrug] = useState(null);
  useEffect(() => {
    if (drugName && !selectedDrug && !firstRender.current) {
      clearTimeout(timeout.current);
      timeout.current = setTimeout(() => {
        drugAutocomplete(dashState, drugName)
          .then((res) => {
            const uniqueDrugs = Array.from(
              new Set(res?.data?.map((drug) => drug.alt_name))
            ).map((alt_name) =>
              res?.data?.find((drug) => drug.alt_name === alt_name)
            );
            setPossibleDrugs(uniqueDrugs || []);
          })
          .catch((e) => {
            console.error(e);
          });
      }, 1000);
    } else {
      clearTimeout(timeout.current);
      setPossibleDrugs([]);
    }
  }, [drugName]);
  useEffect(() => {
    firstRender.current = false;
  }, []);
  return (
    <Box position="relative" w="100%">
      <InputGroup size="sm">
        <Input
          value={drugName}
          placeholder="Search medication..."
          w="100%"
          onChange={(e) => {
            setDrugName(e.target.value);
            if (selectedDrug) setSelectedDrug(null);
          }}
          size="sm"
          variant="outline"
          borderColor="gray.300"
          borderRadius="md"
        />
        <InputRightElement>
          <SearchIcon
            cursor="pointer"
            onClick={async () => {
              if (!drugName) return;
              setSelectedDrug({brand_name: drugName});
            }}
          />
        </InputRightElement>
      </InputGroup>
      {possibleDrugs?.length > 0 && (
        <Box
          position="absolute"
          top="calc(100% + 4px)"
          left={0}
          right={0}
          bg="white"
          border="1px solid"
          borderColor="#e6e6e6"
          borderRadius="8px"
          boxShadow="md"
          maxH="240px"
          overflowY="auto"
          zIndex={20}
        >
          {possibleDrugs.map((item, index) => (
            <Box
              key={index}
              px={3}
              py={2}
              cursor="pointer"
              _hover={{bg: "gray.50"}}
              onClick={() => {
                setSelectedDrug(item);
                setDrugName(item.brand_name);
                onChange(item);
                setPossibleDrugs([]);
              }}
            >
              <Text fontSize="sm" noOfLines={1}>
                {item?.alt_name}
              </Text>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}
